"use client";

import {
  Accordion,
  Badge,
  Breadcrumbs,
  Button,
  Checkbox,
  createTheme,
  Drawer,
  FileInput,
  Input,
  MantineProvider,
  Menu,
  Modal,
  MultiSelect,
  NavLink,
  NumberInput,
  PasswordInput,
  Portal,
  Radio,
  ScrollArea,
  SegmentedControl,
  Select,
  Stepper,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { ReactNode } from "react";

const MantineGlobalLayout = ({ children }: { children: ReactNode }) => {
  const theme = createTheme({
    defaultRadius: "sm",
    radius: {
      xs: "2px",
      sm: "4px",
      md: "6px",
      lg: "8px",
      xl: "12px",
      max: "16px",
    },
    colors: {
      primary: [
        "#EFF6FF",
        "#dbeafe",
        "#bfdbfe",
        "#93c5fd",
        "#60a5fa",
        "#3b82f6",
        "#04305c", // Main Brand Color
        "#032a50",
        "#022441",
        "#011e34",
      ],
      slate: [
        "#F9FAFB",
        "#F3F4F6",
        "#E5E7EB",
        "#D1D5DC",
        "#99A1AF",
        "#6A7282",
        "#45556C",
        "#364153",
        "#1E2939",
        "#101828",
        "#030712",
      ],
    },
    primaryColor: "primary",
    primaryShade: 6,
    fontFamily: "Sora, sans-serif",
    components: {
      // Avoid NotFoundError on login/route unmount: Mantine Portal cleanup calls
      // document.body.removeChild without checking the node is still a child.
      Portal: Portal.extend({
        defaultProps: {
          reuseTargetNode: true,
        },
      }),
      Drawer: Drawer.extend({
        defaultProps: {
          portalProps: { reuseTargetNode: true },
          styles: {
            title: {
              textTransform: "capitalize",
            },
          },
        },
      }),
      Modal: Modal.extend({
        defaultProps: {
          radius: "lg",
          centered: true,
          scrollAreaComponent: ScrollArea.Autosize,
          closeOnEscape: false,
          closeOnClickOutside: false,
          portalProps: { reuseTargetNode: true },
          overlayProps: {
            backgroundOpacity: 0.6,
            blur: 3,
          },
          styles: {
            content: {
              maxWidth: "min(100vw - 32px, var(--modal-size))",
            },
            inner: {
              paddingLeft: 16,
              paddingRight: 16,
            },
            title: {
              textTransform: "capitalize",
            },
          },
        },
      }),
      Text: Text.extend({
        defaultProps: {
          c: "slate.7",
        },
      }),
      Title: Title.extend({
        defaultProps: {
          c: "slate.8",
          fw: 600,
          tt: "capitalize",
        },
      }),
      Table: Table.extend({
        styles: {
          th: {
            textTransform: "capitalize",
          },
        },
      }),
      InputWrapper: Input.Wrapper.extend({
        styles: {
          label: {
            textTransform: "capitalize",
          },
        },
      }),
      TextInput: TextInput.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
            tt: "capitalize",
          },
        },
      }),
      PasswordInput: PasswordInput.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
            tt: "capitalize",
          },
        },
      }),
      Textarea: Textarea.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
            tt: "capitalize",
          },
        },
      }),
      FileInput: FileInput.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
            tt: "capitalize",
          },
        },
      }),

      NumberInput: NumberInput.extend({
        defaultProps: {
          radius: "md",
          stepHoldDelay: 350,
          stepHoldInterval: (stepCount) => Math.max(30, 90 - stepCount * 5),
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
            tt: "capitalize",
          },
        },
      }),
      Select: Select.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
            tt: "capitalize",
          },
        },
      }),
      MultiSelect: MultiSelect.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
            tt: "capitalize",
          },
          descriptionProps: {
            c: "slate.5",
            fz: "12px",
          },
        },

        styles: {
          // Pill container
          pill: {
            backgroundColor: "var(--mantine-color-primary-1)",
            color: "var(--mantine-color-primary-7)",
            fontWeight: 600,
          },
        },
      }),
      DatePickerInput: DatePickerInput.extend({
        defaultProps: {
          labelProps: {
            tt: "capitalize",
          },
        },
      }),

      Button: Button.extend({
        defaultProps: {
          fw: 400,
          fz: "15px",
          size: "md",
          radius: "md",
          tt: "capitalize",
        },
      }),
      NavLink: NavLink.extend({
        styles: {
          label: {
            textTransform: "capitalize",
          },
        },
      }),
      Tabs: Tabs.extend({
        styles: {
          tabLabel: {
            textTransform: "capitalize",
          },
        },
      }),
      Menu: Menu.extend({
        styles: {
          itemLabel: {
            textTransform: "capitalize",
          },
          label: {
            textTransform: "capitalize",
          },
        },
      }),
      Tooltip: Tooltip.extend({
        styles: {
          tooltip: {
            textTransform: "capitalize",
          },
        },
      }),
      Checkbox: Checkbox.extend({
        defaultProps: {
          color: "primary",
        },
        styles: {
          input: {
            borderColor: "var(--mantine-color-primary-6)",
            cursor: "pointer",
          },
          label: {
            textTransform: "capitalize",
          },
        },
      }),
      Badge: Badge.extend({
        defaultProps: {
          radius: "sm",
          fw: 600,
          tt: "capitalize",
        },
      }),
      Accordion: Accordion.extend({
        styles: {
          label: {
            textTransform: "capitalize",
          },
        },
      }),
      Radio: Radio.extend({
        styles: {
          label: {
            textTransform: "capitalize",
          },
        },
      }),
      Switch: Switch.extend({
        styles: {
          label: {
            textTransform: "capitalize",
          },
        },
      }),
      SegmentedControl: SegmentedControl.extend({
        styles: {
          label: {
            textTransform: "capitalize",
          },
        },
      }),
      Breadcrumbs: Breadcrumbs.extend({
        styles: {
          breadcrumb: {
            textTransform: "capitalize",
          },
        },
      }),
      Stepper: Stepper.extend({
        styles: {
          stepLabel: {
            textTransform: "capitalize",
          },
        },
      }),
    },
  });

  return <MantineProvider theme={theme}>{children}</MantineProvider>;
};

export default MantineGlobalLayout;
